import { t } from "i18next";
import type {
  ConditionsConfig,
  OutputDescription,
} from "@smartsuite/automation-engine-api";
import { ConditionsLogicOperator } from "@smartsuite/automation-engine-api";
import { conditionsText } from "../../../../../components/PhraseBuilder/conditions";
import { translatePhraseNode } from "../../../../../components/PhraseBuilder/helper";
import type { PhraseDirectory } from "../../../../../components/PhraseBuilder/helper";
import { translateAction } from "../../../../../locales/i18n";
import { getOutputDescriptions } from "../../../../../utils/conditions-coverter.hook";
import { ActionLabel } from "../../../../../utils/constants";
import { resolveReferencedConditionField } from "../../../../AutomationForm/Sections/TriggerSection/ConditionsForm/ConditionItem/left-source/resolve-referenced-condition-field";
import {
  conditionReferences,
  conditionValueReferences,
  referenceKey,
} from "./resolve-matched-values";
import type { ReferenceValueResolver } from "./resolve-matched-values";
import type { CandidateStore } from "../../../../../stores/automation/candidate.store";
import type { LoopScope } from "../../../../../stores/automation/looping.store";
import type {
  ActionCandidate,
  TriggerCandidate,
} from "../../../../../types/candidate";

// eslint-disable-next-line import/no-unused-modules -- consumed by Task 6's BranchCard
export interface ConditionLine {
  text: string;
  connector?: "AND" | "OR";
}

const isDefined = <T>(value: T | undefined): value is T => value !== undefined;

const isActionCandidate = (
  entity: TriggerCandidate | ActionCandidate,
): entity is ActionCandidate => "actionReference" in entity;

/**
 * instanceId of the action whose fields the enclosing loop's Current Item
 * exposes, or undefined when the scope has no Current Item. Mirrors the
 * eligibility rule of use-condition-left-sources.ts's currentItemLeftSource:
 * only a find-records record-list loop iterates single records addressable
 * per item, so only that source ever produced a Current Item pick -- a
 * reference to any other action can't have been one and must keep its plain
 * action label.
 */
const currentItemInstanceIdOf = (
  scope: LoopScope | undefined,
): number | undefined => {
  const source = scope?.source;
  if (
    source?.oneofKind !== "recordList" ||
    source.recordList.recordList.oneofKind !== "actionReference" ||
    source.recordList.recordList.actionReference.actionId !==
      ActionLabel.findRecords
  ) {
    return undefined;
  }
  return source.recordList.recordList.actionReference.instanceId;
};

/**
 * Spec 13.6 source labels: trigger -> "Trigger"; the enclosing loop's record
 * source action -> "Current Item"; any other action -> its custom name when
 * set, else its system label (translateAction already applies that fallback
 * given the action itself).
 */
const deriveSourceLabel = (
  sourceEntity: TriggerCandidate | ActionCandidate,
  currentItemInstanceId: number | undefined,
): string => {
  if (!isActionCandidate(sourceEntity)) {
    return t("general.trigger.one");
  }
  if (
    currentItemInstanceId !== undefined &&
    sourceEntity.actionReference?.instanceId === currentItemInstanceId
  ) {
    return t("general.current-item");
  }
  return translateAction(
    {
      actionId: sourceEntity.actionReference?.actionId ?? "",
      label: sourceEntity.description.label,
    },
    sourceEntity,
  );
};

/**
 * Formats a branch's conditions into display lines per spec 13.5/13.6:
 * "[Source].[Field] {operator phrase} {right side}", one line per condition,
 * with an AND/OR connector (from conditions.operator) suffixing every line
 * but the last. A condition whose left-side field can no longer be resolved
 * (deleted field/action) is skipped rather than thrown -- deleted-field jobs
 * must still render their surviving conditions.
 *
 * `branchesGroupInstanceId` is the instanceId of the branches block the
 * conditions belong to. A Current Item pick is stored on the wire as a plain
 * actionReference to the enclosing loop's record-list source action (see
 * use-condition-left-sources.ts's currentItemLeftSource), so telling it apart
 * from a direct reference to that same action needs the block's position:
 * candidate.looping.scopeForGroup(instanceId) resolves the loop enclosing the
 * block, and only a reference to THAT loop's source action is labeled
 * "Current Item". When the id is omitted, no Current Item labeling happens.
 *
 * Only flat conditions (group.group.oneofKind === "condition") are rendered;
 * nested condition sub-groups (oneofKind === "config") are skipped, matching
 * the same limitation in PhraseBuilder/conditions.ts's getConditionTexts (the
 * branch condition builder doesn't produce nested groups today).
 */
/**
 * instanceId of the enclosing loop's Current Item source for a branches block
 * -- shared by the condition lines and the matched-values lines so both label
 * the same references "Current Item".
 */
const currentItemInstanceIdForBlock = (
  candidate: CandidateStore,
  branchesGroupInstanceId: number | undefined,
): number | undefined =>
  branchesGroupInstanceId !== undefined
    ? currentItemInstanceIdOf(
        candidate.looping.scopeForGroup(branchesGroupInstanceId),
      )
    : undefined;

export const formatBranchConditions = (
  conditions: ConditionsConfig,
  candidate: CandidateStore,
  branchesGroupInstanceId?: number,
  /** Workspace members/teams. A member/team comparison value is stored as an
   * id-only value function, so without these it renders empty (STR-14493). */
  directory?: PhraseDirectory,
): ConditionLine[] => {
  const connector: "AND" | "OR" =
    conditions.operator === ConditionsLogicOperator.OPERATOR_OR ? "OR" : "AND";

  const currentItemInstanceId = currentItemInstanceIdForBlock(
    candidate,
    branchesGroupInstanceId,
  );

  // Date values inside a phrase are rendered in the automation's own timezone,
  // matching PhraseBuilder's automation description.
  const timezone = candidate.self.timezone ?? "UTC";

  // Right-side values in a branch condition may reference the trigger's or
  // any upstream action's outputs, unlike a trigger condition (which can only
  // ever reference the trigger itself) -- so the lookup list spans all of
  // them, not just the resolved left-side source.
  const outputs = [
    ...getOutputDescriptions(candidate.self.trigger, true),
    ...candidate.allActions.flatMap((action) =>
      getOutputDescriptions(action, true),
    ),
  ];

  const texts = conditions.group
    .map((entry) => {
      if (entry.group.oneofKind !== "condition") return undefined;
      const condition = entry.group.condition;
      if (condition.function?.function.oneofKind === undefined) {
        return undefined;
      }

      // Branch conditions store the left side as an outputReference; legacy
      // Trigger/Find conditions still use conditionFieldId (see
      // PhraseBuilder/conditions.ts's getConditionTexts for the same split).
      const resolved =
        condition.outputReference !== undefined
          ? resolveReferencedConditionField(
              candidate,
              condition.outputReference,
            )
          : undefined;

      const conditionField =
        resolved?.fieldDescription ??
        candidate.self.trigger.description.conditionFields.find(
          (description) =>
            description.conditionFieldId === condition.conditionFieldId,
        );

      if (conditionField === undefined || conditionField.editor === undefined) {
        return undefined;
      }

      const sourceLabel =
        resolved !== undefined
          ? deriveSourceLabel(resolved.sourceEntity, currentItemInstanceId)
          : t("general.trigger.one");

      // Per the Run History design the condition line renders at three weights:
      // the source prefix and brackets stay regular, the field NAME is medium,
      // and the comparison value is regular. The shared phrase-builder templates
      // wrap both the field label and (where present) the value in <strong>
      // (`<strong>{{conditionFieldLabel}}</strong> … <strong>{{right}}</strong>`),
      // which renders bold. We therefore leave the templates untouched and
      // re-mark the field ourselves below.
      // PhraseBuilder resolves a right-side reference by outputId alone and
      // prints the matched output's bare label -- so a field id shared by two
      // sources (Trigger.Title vs Find.Title, the spec's own example) rendered
      // the first match with no source at all. Resolve THIS condition's own
      // right-side references here, where the reference still carries its
      // source, and put the resulting "[Source].[Field]" entries first so the
      // by-id lookup lands on them (BUG-21770).
      const rightSideOutputs = conditionValueReferences(condition).flatMap(
        (reference): OutputDescription[] => {
          const target = resolveReferencedConditionField(candidate, reference);
          if (target?.fieldDescription === undefined) return [];
          const label = deriveSourceLabel(
            target.sourceEntity,
            currentItemInstanceId,
          );
          // Only outputId/label are read by PhraseBuilder's reference lookup;
          // the rest of OutputDescription is irrelevant to it.
          const prefixed: Partial<OutputDescription> = {
            outputId: reference.outputId,
            label: `[${label}].[${target.fieldDescription.label}]`,
          };
          return [prefixed as OutputDescription];
        },
      );

      const phrase = conditionsText(
        condition.function,
        conditionField.label,
        [...rightSideOutputs, ...outputs],
        conditionField.editor,
        directory,
      );
      if (typeof phrase === "string") {
        // Only the empty/no-op phrase is returned as a string; it is filtered
        // out below, so there is nothing to decorate.
        return phrase;
      }
      // translatePhraseNode, not a bare t(): a phrase's `data` can itself hold
      // phrase nodes -- with several selected values the 2nd+ arrive as nested
      // "comma" nodes, which a single-level t() renders "[object Object]"
      // (BUG-21273).
      //
      // Every condition template starts with `<strong>{{conditionFieldLabel}}
      // </strong>`, so the first <strong>…</strong> pair is the field name:
      // retag it <field> (BranchCard renders that medium-weight) and close the
      // bracket after it. Any later <strong> wraps the comparison value -- the
      // design wants it at the line's regular weight, so strip that emphasis so
      // it inherits. Finally prepend the regular-weight source prefix.
      const phraseText = translatePhraseNode(phrase, timezone)
        .replace("<strong>", "<field>")
        .replace("</strong>", "</field>]")
        .replace(/<\/?strong>/g, "");
      return `[${sourceLabel}].[${phraseText}`;
    })
    .filter(isDefined)
    .filter((text) => text !== "");

  return texts.map((text, index) => ({
    text,
    connector: index < texts.length - 1 ? connector : undefined,
  }));
};

// eslint-disable-next-line import/no-unused-modules -- consumed by BranchCard's matched-conditions box
export interface MatchedConditionLine {
  /** Stable identity of the referenced output (source + outputId). */
  key: string;
  /**
   * "Trigger" / "Current item" / action name; undefined when the current
   * schema no longer resolves the source (the row falls back to the job's
   * stored output label alone).
   */
  sourceLabel?: string;
  fieldLabel: string;
  value: string;
}

/**
 * Matched Conditions rows per the Run History design: one line per reference
 * the branch's conditions were evaluated against (each condition's left side
 * plus its right-side compare-to-field references) -- rendered by BranchCard
 * as "[Source].[Field]: Value" with the field name medium-weight, as
 * STRUCTURED PARTS rather than a markup string, so record values are never
 * parsed for tags. Source/field labels are derived exactly like
 * formatBranchConditions' condition lines (same reference resolution, same
 * Current Item scoping); when the field no longer resolves in the current
 * schema (deleted field / edited automation) the row falls back to the
 * stored output's own label from the resolver instead of disappearing --
 * historic jobs keep showing what they matched on. Value resolution is
 * injected: GetBranchData outputs on the api-7.25 path, a job-tree walk on
 * the legacy path. Anything without a resolvable value is skipped rather
 * than thrown, and repeated references dedupe to one row.
 */
export const formatMatchedConditionValues = (
  conditions: ConditionsConfig,
  candidate: CandidateStore,
  branchesGroupInstanceId: number | undefined,
  resolveValue: ReferenceValueResolver,
): MatchedConditionLine[] => {
  const currentItemInstanceId = currentItemInstanceIdForBlock(
    candidate,
    branchesGroupInstanceId,
  );

  const lines = conditionReferences(conditions).flatMap(
    (reference): MatchedConditionLine[] => {
      const resolvedValue = resolveValue(reference);
      if (resolvedValue === undefined) {
        return [];
      }
      const resolved = resolveReferencedConditionField(candidate, reference);
      const fieldLabel =
        resolved?.fieldDescription?.label ?? resolvedValue.outputLabel;
      if (fieldLabel.trim() === "") {
        return [];
      }
      const sourceLabel =
        resolved !== undefined
          ? deriveSourceLabel(resolved.sourceEntity, currentItemInstanceId)
          : reference.source.oneofKind === "triggerReference"
            ? String(t("general.trigger.one"))
            : undefined;
      return [
        {
          key: referenceKey(reference),
          sourceLabel,
          fieldLabel,
          value: resolvedValue.value,
        },
      ];
    },
  );

  return lines.filter(
    (line, index) => lines.findIndex((l) => l.key === line.key) === index,
  );
};
